import { call, takeEvery, put, select } from 'redux-saga/effects'
import { buyFormActions, BuyFormActionTypes } from '@buy/state/buy-form/actions'
import { fatalErrorActions } from '@common/state/fatal-error/actions'
import { BuyCheckoutState } from '@buy/state/checkout/state'
import { getCartItems } from '@buy/utils/get-item-values'
import { getCheckoutState, getUserState, getContentData, getCouponState, getPlanData } from '@buy/state/selectors'
import { getLocationState, getAccountState } from '@common/state/selectors'
import { MarketoUserInfo } from '@common/state/marketing/state'
import { marketingActions } from '@common/state/marketing/actions'
import { BuyContentData } from '@buy/state/content-data'
import { CHECKOUTFORM_STEPS, BUY_TRACKING_COMPONENTS } from '@buy/constants'
import { LocationState } from '@common/state/location/state'
import { BillingPeriodToBOSSBillingPeriodMapper, TRACKING_EVENTS, VIEW_PATH } from '@common/constants'
import { buyTrackingActions } from '@buy/state/tracking/actions'
import {
    ApplyPromoCodeRequest,
    BuyUserState,
    CreateCartRequest,
    CreateV3CartRequestCartItem,
    CreateCartResponse,
    SetupTransactionRequest,
    SetupTransactionRequestProduct,
    SetupTransactionResponse,
} from '@buy/state/user/state'
import { ExistingUserInfoType, ExistingBillingInfoType } from '@common/state/user/state'
import { BuyPlan } from '@buy/state/plan'
import { BuyCouponState } from '@buy/state/coupon/state'
import { isEmpty } from 'lodash-es'
import { getSubscriptionInfo } from '@buy/utils'
import { getTokenFromLocalStorage } from '@common/utils'
import { AxiosResponse } from 'axios'
import { applyPromoCode, createCartV3, deleteCart, setupTransaction, updateBillingAddressUCS } from '@buy/services'
import { getContentLanguage } from '@buy/utils/language-helpers'
import { getOnePayLocaleFromContentLanguage } from '@buy/utils/get-onepay-locale'
import { UpdateBillingAddressRequest } from '@buy/state/update-billing-address/state'

function* submitFormSaga(action: ReturnType<typeof buyFormActions.existingUserSubmitFormUcs>) {
    const componentName = BUY_TRACKING_COMPONENTS.EXISTING_USER_FORM_SUBMISSION_SAGA
    const steps: Record<CHECKOUTFORM_STEPS, boolean> = {
        [CHECKOUTFORM_STEPS.CREATE_FLEX_TOKEN]: true,
        [CHECKOUTFORM_STEPS.CARDINAL_TRIGGER_BIN_PROCESS]: true,
        [CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT]: true,
        [CHECKOUTFORM_STEPS.SAVE_BILLING_INFO]: true,
        [CHECKOUTFORM_STEPS.SAVE_PAYMENT_INFO]: true,
        [CHECKOUTFORM_STEPS.CARDINAL_SEND_CHALLENGE]: false,
        [CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS]: false,
        [CHECKOUTFORM_STEPS.SUBMIT_PAYMENT]: false,
    }
    // update the redux store to ensure consistency
    yield put(
        buyFormActions.updateSteps(CHECKOUTFORM_STEPS.CREATE_FLEX_TOKEN, steps[CHECKOUTFORM_STEPS.CREATE_FLEX_TOKEN])
    )
    yield put(
        buyFormActions.updateSteps(
            CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT,
            steps[CHECKOUTFORM_STEPS.CREATE_USER_ACCOUNT]
        )
    )
    yield put(
        buyFormActions.updateSteps(CHECKOUTFORM_STEPS.SAVE_BILLING_INFO, steps[CHECKOUTFORM_STEPS.SAVE_BILLING_INFO])
    )
    yield put(
        buyFormActions.updateSteps(CHECKOUTFORM_STEPS.SAVE_PAYMENT_INFO, steps[CHECKOUTFORM_STEPS.SAVE_PAYMENT_INFO])
    )

    const checkout: BuyCheckoutState = yield select(getCheckoutState)
    const { locationInfo }: LocationState = yield select(getLocationState)

    const userState: BuyUserState = yield select(getUserState)
    const {
        addressLine1 = '',
        city = '',
        postalCode = '',
        state: stateCode = '',
        phoneNumber = '',
    } = userState.existingBillingInformation as ExistingBillingInfoType

    const {
        UserName,
        Name: { FamilyName, GivenName },
        UserKey,
        AccountKey,
    } = userState.existingUserInformation as ExistingUserInfoType

    const { submittoMarketo, marketoProductName, marketoSalesBrief, marketoSalesforceCampaignId }: BuyContentData =
        yield select(getContentData)

    const planData: BuyPlan = yield select(getPlanData)
    const selectedPlan: BuyPlan = planData
    const coupon: BuyCouponState = yield select(getCouponState)
    const { isCouponValidAndApplied, couponServerData } = coupon
    // const couponServerData: CouponServerData | null = yield call(getActiveCouponServerData, coupon, planData)
    const isCouponValid = isCouponValidAndApplied && couponServerData && !isEmpty(couponServerData)
    const { CompanyName } = action.payload.values
    let transactionSessionId = ''

    try {
        const { accountSubscriptions } = yield select(getAccountState)
        const productFamilyWithSpace = marketoProductName.replace('GoTo', 'GoTo ')
        const contentLanguage = getContentLanguage()

        const subscriptionInfo = getSubscriptionInfo(
            checkout.billingTenure.selected,
            selectedPlan,
            planData,
            accountSubscriptions
        )

        // #region if CompanyName is provided, update billing info
        if (CompanyName && userState.existingBillingInformation) {
            const { country, firstName, lastName, email } = userState.existingBillingInformation
            const billingData: UpdateBillingAddressRequest = {
                billingAddress: {
                    addressLine1,
                    city,
                    state: stateCode || null,
                    country,
                    postalCode: postalCode || null,
                    firstName,
                    lastName,
                    email,
                    phoneNumber,
                    companyName: CompanyName,
                },
            }

            const token = getTokenFromLocalStorage()
            const userToken = token?.access_token
            try {
                yield call(updateBillingAddressUCS as any, userToken, AccountKey, billingData)
            } catch (e: any) {
                yield put(
                    buyTrackingActions.track(
                        {
                            event: TRACKING_EVENTS.ERROR_PURCHASE,
                            eventData: { errorMessage: 'failed to save billing info' },
                        },
                        componentName
                    )
                )
                yield put(fatalErrorActions.setFatalError(true, { trackErrorMessage: 'failed to save billing info' }))
                return
            }
        }
        // #endregion

        const items = getCartItems(
            checkout.baseLine,
            checkout.additionalLines,
            checkout.addonLines,
            subscriptionInfo,
            checkout.billingTenure.selected
        )

        // #region get available payment menthods
        steps[CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS] = true
        yield put(
            buyFormActions.updateSteps(
                CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS,
                steps[CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS]
            )
        )
        // #endregion

        // #region process purchase - 1st purchase call
        if (steps[CHECKOUTFORM_STEPS.GET_AVAILABLE_PAYMENT_METHODS]) {
            // #region Cart and Transaction
            const promoCode = isCouponValid ? couponServerData?.couponCode : ''
            const token = getTokenFromLocalStorage()!

            // Delete any existing cart
            const deleteCartResponse: AxiosResponse<boolean> = yield call(
                deleteCart as any,
                token.access_token,
                AccountKey
            )
            if (!(deleteCartResponse.status === 404 || deleteCartResponse.status === 200)) {
                // 404 - Commerce cart not found, so you're got to create a new cart
                throw new Error('Error while invoking DELETE v2/cart') // throw an exception if any other errors in the API
            }

            // Create Cart
            const cartItems = items.map((item) => {
                const cartItem: CreateV3CartRequestCartItem = {
                    paymentPlan: BillingPeriodToBOSSBillingPeriodMapper[checkout.billingTenure.selected],
                    productFamilyKey: productFamilyWithSpace,
                    productSku: item.productSku,
                    quantity: item.quantity,
                }
                return cartItem
            })
            const cartRequestData: CreateCartRequest = {
                currency: locationInfo.currency,
                cartItems,
                promoCode,
            }

            const createCartResponse: AxiosResponse<CreateCartResponse> = yield call(
                createCartV3 as any,
                token.access_token,
                AccountKey,
                cartRequestData
            )
            const createCartResponseData = createCartResponse.data

            // Apply promoCode (if any) to the cart
            if (promoCode) {
                const applyPromoCodeRequestData: ApplyPromoCodeRequest = {
                    promoCode,
                }
                const applyPromoCodeResponse: AxiosResponse = yield call(
                    applyPromoCode as any,
                    token.access_token,
                    AccountKey,
                    applyPromoCodeRequestData
                )
                if (applyPromoCodeResponse.status !== 200) {
                    // TODO: WAE1-4010: Errror handling?
                }
            }

            // Setup Transaction
            const totalAfterTax = createCartResponseData.cartTotal + checkout.calculatedTax
            const transactionProducts = createCartResponseData.cartItems.map((cartItem) => {
                const product: SetupTransactionRequestProduct = {
                    amount: totalAfterTax,
                    flowName: null,
                    frequency: cartItem.frequency,
                    productFamily: cartItem.productFamilyKey,
                    productSku: cartItem.productSku,
                    publisherId: null,
                    quantity: cartItem.quantity,
                    subscriptionEndDate: null,
                    transactionType: 'CUSTOMER_ACTION',
                }
                return product
            })
            const successRedirectUrl =
                window.location.origin +
                window.location.pathname.replace(VIEW_PATH.PATH_CHECKOUT, VIEW_PATH.PATH_CONFIRMATION)
            const setupTransactionRequestData: SetupTransactionRequest = {
                quoteId: createCartResponseData.key,
                intent: 'PaymentOrPaymentMethodChange',
                currencyCode: locationInfo.currency,
                targetOrigin: process.env.ONEPAY_SITE_URL!,
                amount: checkout.calculatedTotal,
                cancelRedirectUrl: window.location.href,
                products: transactionProducts,
                successRedirectUrl,
                taxAmount: checkout.calculatedTax,
            }
            const setupTransactionResponse: AxiosResponse<SetupTransactionResponse> = yield call(
                setupTransaction as any,
                token.access_token,
                AccountKey,
                setupTransactionRequestData
            )
            const setupTransactionResponseData = setupTransactionResponse.data

            // Save userData.accountKey to sessionStorage so we can retrieve it after coming back from OnePay
            // See where it is retrieved in src/products/buy/saga/checkout-form/get-latest-invoice.ts
            window.sessionStorage.setItem('accountKey', AccountKey)

            // Save setupTransactionResponseData.sessionId to sessionStorage so we can retrieve it after coming back from OnePay
            // See where it is retrieved in src/products/buy/saga/checkout-form/get-latest-invoice.ts
            transactionSessionId = setupTransactionResponseData.sessionId
            window.sessionStorage.setItem('transactionSessionId', transactionSessionId)

            // Save wasExistingUserCheckout:'true' to sessionStorage so we can retrieve it after coming back from OnePay
            // See where it is retrieved in src/products/buy/views/Confirmation/connector.tsx
            window.sessionStorage.setItem('wasExistingUserCheckout', 'true')
            // #endregion Cart and Transaction

            // Proceed to post-submit_payment steps
            steps[CHECKOUTFORM_STEPS.SUBMIT_PAYMENT] = true
            yield put(
                buyTrackingActions.track(
                    { event: TRACKING_EVENTS.PURCHASE, eventData: { invoiceNumber: null } },
                    componentName,
                    true // true to save to sessionStorage instead of dataLayer for later use on confirmation page which will then push it to the dataLayer
                )
            )
            yield put(
                buyFormActions.updateSteps(CHECKOUTFORM_STEPS.SUBMIT_PAYMENT, steps[CHECKOUTFORM_STEPS.SUBMIT_PAYMENT])
            )
        }
        // #endregion

        // #region Set purchase complete
        if (steps[CHECKOUTFORM_STEPS.SUBMIT_PAYMENT]) {
            yield put(
                buyTrackingActions.track(
                    { event: TRACKING_EVENTS.COMPLETE_BUY_FLOW },
                    componentName,
                    true // true to save to sessionStorage instead of dataLayer for later use on confirmation page which will then push it to the dataLayer
                )
            )
            if (submittoMarketo) {
                const { planKey } = planData ?? {}
                const marketoData: MarketoUserInfo = {
                    userKey: UserKey,
                    accountKey: AccountKey,
                    firstName: GivenName,
                    lastName: FamilyName,
                    email: UserName,
                    planKey,
                    address: addressLine1,
                    city,
                    state: stateCode,
                    zipcode: postalCode,
                    phone: phoneNumber,
                    salesBrief: marketoSalesBrief,
                    campaignID: marketoSalesforceCampaignId,
                    product: marketoProductName,
                    company: CompanyName,
                }

                // Save marketoData to sessionStorage so we can post it to AWS after coming back from OnePay
                // See where it is retrieved from session and posted to AWS in src/products/buy/views/Confirmation/index.tsx
                const saveMarketoToSessionStorage = true
                yield put(marketingActions.postToMarketo(marketoData, saveMarketoToSessionStorage))
            }

            // Redirect to OnePay
            const onePayTheme = 'light' // other option is 'dark'
            const onePayLocale = getOnePayLocaleFromContentLanguage(contentLanguage)
            const onePayUrl = `${process.env.ONEPAY_SITE_URL}/${AccountKey}/${transactionSessionId}/pay?locale=${onePayLocale}&theme=${onePayTheme}`
            window.location.href = onePayUrl
            return
        }
        // #endregion

        // Trigger form submission error if any of the steps are missed
        yield put(
            fatalErrorActions.setFatalError(true, {
                trackErrorMessage: 'existing user form submission failed',
                fatalErrorType: undefined,
            })
        )
    } catch (e) {
        yield put(fatalErrorActions.setFatalError(true, { trackErrorMessage: 'existing user form submission failed' }))
    }
}

function* initializeSubmitFormSaga() {
    yield takeEvery(BuyFormActionTypes.EXISTING_USER_SUBMIT_BUY_FORM_UCS, submitFormSaga)
}

export default initializeSubmitFormSaga
